import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SHOP_DROPDOWN, CALENDAR_DROPDOWN } from '../components/navConfig';

const NAV_ITEMS = [
  { to: '/', label: 'Home' },
  { label: 'Shop', dropdown: SHOP_DROPDOWN('') },
  CALENDAR_DROPDOWN(''),
  { to: '/archive', label: 'Archive' },
  { to: '/about', label: 'About', active: true },
];

const FOOTER_LEGAL_LINKS = [
  { to: '/privacy', label: 'Privacy' },
  { to: '/terms', label: 'Terms' },
  { to: '/shipping', label: 'Shipping' },
  { to: '/faq', label: 'FAQ' },
];

const aboutContent = {
  intro: {
    greeting: "Hi, I'm Roy Edlund, the OBX Beach Bum.",
    sandieIntro:
      "That pretty lady with her arms wrapped around me is Sandie, my girlfriend of soon to be 45 years. Without her, I'd just be some old guy wandering the beach aimlessly, carrying a couple of cameras and muttering to myself. And if I'm being honest, you could say I am that old guy, but I certainly wouldn't be as happy.",
    together:
      "Together we are the OBX Beach Bum. And I owe any success I've had as a photographer to her. Along with her love and support, she's the one that keeps the bills paid, the packages flowing. She keeps our business going. All I do is take photos and then wax eloquent as a beach bum philosopher.",
  },
  howStarted: {
    heading: "How This All Started",
    paragraphs: [
      'After losing everything in 2008, we struggled for a bit and before I knew it depression had taken a strong hold on me. Then one sunny afternoon, as we sat on our bed mapping our future, I said to her, "look, if we\'re gonna be poor, let\'s be poor at the beach."',
      "So, we packed up and moved to the Outer Banks in 2012.",
      "We didn't know anybody and I didn't really have any kind of plan. I just knew that when I was walking on the beach, I was at peace.",
    ],
  },
  beachChanged: {
    heading: "The Beach Changed Me",
    paragraphs: [
      "I spent nearly every day on the beach, rain or shine, carrying my old camera and shooting whatever caught my attention. That first year I took over 97,000 photos on that camera shooting anything that caught my eye, trying to find my voice. I loved everything about this beach bum life.",
      "I realized pretty quick I was changing.",
      "Photography was healing me. The pace of my life slowed, inner peace and joyfulness became the new normal. I was gradually becoming the man I've always wanted to be.",
      "I was becoming whole.",
    ],
  },
  newLife: {
    heading: "My New Life",
    paragraphs: [
      'Soon I had too many photos to just store them on my hard drive. I wanted to share them with anyone that might be interested. So, I started posting them online under my new persona of the OBX Beach Bum. The name stuck and people would stop me saying, "You\'re that beach bum."',
      'It wasn\'t long before people began asking to buy prints, to which I replied, "sure I can do that." I never intended to be a photographer, and certainly never thought I\'d sell my photos, I didn\'t even know how to do it, but I realized I was embarking down a new path.',
      "So, a new life began and we've made so many friends. The OBX Beach Bum continues to grow and I'm so glad you are a part of it.",
    ],
    signoff: "Love from here. ❤",
  },
};

const paragraphStyle = {
  fontSize: "1.05rem",
  lineHeight: 1.85,
  marginBottom: "1.1rem",
  color: "#3a3028",
};

function SectionHeading({ children }) {
  return (
    <h2
      style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: "1.5rem",
        fontWeight: 600,
        color: "#2c2418",
        marginBottom: "1.2rem",
        marginTop: "0.5rem",
      }}
    >
      {children}
    </h2>
  );
}

function Divider() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "1.5rem 0 2rem",
        gap: "12px",
      }}
    >
      <div style={{ height: "1px", width: "60px", background: "#c49a6c", opacity: 0.5 }} />
      <div
        style={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          background: "#c49a6c",
          opacity: 0.6,
        }}
      />
      <div style={{ height: "1px", width: "60px", background: "#c49a6c", opacity: 0.5 }} />
    </div>
  );
}

function About() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(true);
  }, []);

  return (
    <div className="substack-site">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Playfair+Display:wght@600;700&display=swap');
      `}</style>

      {/* Header */}
      <Header navItems={NAV_ITEMS} />

      {/* Main Content */}
      <main
        style={{
          background: "#ffffff",
          fontFamily: "'Libre Baskerville', 'Georgia', serif",
          color: "#3a3028",
        }}
      >
        <div
          style={{
            maxWidth: "680px",
            margin: "0 auto",
            padding: "2rem 1.5rem 4rem",
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(12px)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
          }}
        >
          {/* Page Title */}
          <h1
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: "2.4rem",
              fontWeight: 700,
              color: "#2c2418",
              marginBottom: "1.5rem",
              textAlign: "center",
              letterSpacing: "0.02em",
            }}
          >
            About
          </h1>

          {/* Photo of Roy and Sandie */}
          <div style={{ textAlign: "center", marginBottom: "2rem" }}>
            <img
              src="https://daily-obx-cdn.b-cdn.net/newsletter-images/menher.jpg"
              alt="Roy and Sandie Edlund, the OBX Beach Bum"
              style={{
                maxWidth: "100%",
                width: "500px",
                borderRadius: "6px",
              }}
            />
          </div>

          {/* Intro Section */}
          <section style={{ marginBottom: "2.5rem" }}>
            <p
              style={{
                fontSize: "1.25rem",
                fontWeight: 700,
                lineHeight: 1.6,
                marginBottom: "1.2rem",
                color: "#2c2418",
              }}
            >
              {aboutContent.intro.greeting}
            </p>
            <p style={paragraphStyle}>{aboutContent.intro.sandieIntro}</p>
            <p style={paragraphStyle}>{aboutContent.intro.together}</p>
          </section>

          <Divider />

          {/* How This All Started */}
          <section style={{ marginBottom: "2.5rem" }}>
            <SectionHeading>{aboutContent.howStarted.heading}</SectionHeading>
            {aboutContent.howStarted.paragraphs.map((p, i) => (
              <p key={i} style={paragraphStyle}>{p}</p>
            ))}
          </section>

          <Divider />

          {/* The Beach Changed Me */}
          <section style={{ marginBottom: "2.5rem" }}>
            <SectionHeading>{aboutContent.beachChanged.heading}</SectionHeading>
            {aboutContent.beachChanged.paragraphs.map((p, i) => (
              <p
                key={i}
                style={{
                  ...paragraphStyle,
                  ...(p === "I was becoming whole."
                    ? {
                        fontStyle: "italic",
                        fontSize: "1.15rem",
                        color: "#5a4a38",
                        marginTop: "0.5rem",
                      }
                    : {}),
                }}
              >
                {p}
              </p>
            ))}
          </section>

          <Divider />

          {/* My New Life */}
          <section style={{ marginBottom: "2rem" }}>
            <SectionHeading>{aboutContent.newLife.heading}</SectionHeading>
            {aboutContent.newLife.paragraphs.map((p, i) => (
              <p key={i} style={paragraphStyle}>{p}</p>
            ))}
          </section>

          {/* Sign-off */}
          <p
            style={{
              fontSize: "1.15rem",
              fontStyle: "italic",
              color: "#6a5a48",
              textAlign: "center",
              marginTop: "2.5rem",
              letterSpacing: "0.01em",
            }}
          >
            {aboutContent.newLife.signoff}
          </p>

          <div className="back-link" style={{ marginTop: "3rem", textAlign: "center" }}>
            <Link to="/">← Back to Home</Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer showLogo={false} legalLinks={FOOTER_LEGAL_LINKS} />
    </div>
  );
}

export default About;
